import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useAxios from '../utils/useAxios';
import Swal from 'sweetalert2';
import { FaSearch, FaCalendarAlt, FaDownload, FaSignInAlt, FaSignOutAlt, FaTimes } from 'react-icons/fa';
import { useSidebar } from '../context/SidebarContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import styles from '../styles/PresenceExceptionnellePage';
import OrganisationSelector from '../components/OrganisationSelector';
import { useAuth } from '../context/AuthContext';

const PresencesExceptionnellesPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [presences, setPresences] = useState([]);
  const [pagination, setPagination] = useState({ totalPages: 1, count: 0, next: null, previous: null });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const { sidebarVisible } = useSidebar();
  const axiosInstance = useAxios();
  const { user } = useAuth();
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Fonction pour charger les détails d'une organisation
  const fetchOrganisationDetails = async (orgId) => {
    try {
      const response = await axiosInstance.get(`/organisations/${orgId}/`);
      setCurrentOrganisation(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement de l'organisation", error);
    }
  };

  // Effet pour initialiser les filtres depuis l'URL
  useEffect(() => {
    const orgParam = searchParams.get('organisation');
    const dateParam = searchParams.get('date');
    const typeParam = searchParams.get('type');
    const searchParam = searchParams.get('search');
    
    if (orgParam) {
      setSelectedOrganisation(orgParam);
      fetchOrganisationDetails(orgParam);
    }
    
    if (dateParam) {
      setSelectedDate(dateParam);
    }
    
    if (typeParam) {
      setSelectedType(typeParam);
    }
    
    if (searchParam) {
      setSearch(searchParam);
    }
  }, [searchParams]);

  // Effet pour mettre à jour l'URL quand les filtres changent (optionnel)
  useEffect(() => {
    if (selectedOrganisation) {
      const params = new URLSearchParams();
      if (selectedOrganisation) params.set('organisation', selectedOrganisation);
      if (selectedDate) params.set('date', selectedDate);
      if (selectedType) params.set('type', selectedType);
      if (search) params.set('search', search);
      
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [selectedOrganisation, selectedDate, selectedType, search, navigate]);

  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    fetchPresences(1, orgId);
  };

  const openImageModal = (presence) => {
    setSelectedImage({
      src: presence.photo,
      name: `${presence.employe_prenom} ${presence.employe_nom}`
    });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const fetchPresences = async (targetPage = 1, orgId = selectedOrganisation) => {
    if (!orgId) return; 
    
    setLoading(true);
    try {
      const params = {
        page: targetPage,
        organisation: orgId,
        ...(selectedDate && { date: selectedDate }),
        ...(selectedType && selectedType.trim() !== '' && { type: selectedType }),
        ...(search && search.trim() !== '' && { search: search.trim() }),
      };

      console.log('Paramètres envoyés:', params);

      const response = await axiosInstance.get('/pointage/presence-exceptionnelle/', { params });

      console.log('Réponse API:', response.data);

      const formattedPresences = response.data.results.map(item => ({
        ...item,
        employe_nom: item.employe_nom || 'Inconnu',
        employe_prenom: item.employe_prenom || 'Inconnu',
        activite_nom: item.activite_nom || 'Non spécifiée',
        date: item.date || '',
        heure: item.heure || '',
        photo: item.photo || '/default-avatar.png'
      }));

      setPresences(formattedPresences);
      setPagination({
        totalPages: Math.ceil(response.data.count / 10),
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous
      });
      setPage(targetPage);
    } catch (error) {
      console.error('Erreur détaillée:', {
        config: error.config,
        response: error.response,
        message: error.message
      });
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.error || "Impossible de charger les présences exceptionnelles.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOrganisation) {
      fetchPresences(1);
    }
  }, [selectedDate, selectedType, search, selectedOrganisation]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchPresences(newPage);
    }
  };

  const fetchAllPresences = async () => {
    try {
      let allPresences = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams({
          page: currentPage,
          organisation: selectedOrganisation,
          ...(selectedDate && { date: selectedDate }),
          ...(selectedType && { type: selectedType }),
          ...(search && { search }),
        });
        
        const response = await axiosInstance.get(`/pointage/presence-exceptionnelle/?${params}`);
        allPresences = [...allPresences, ...response.data.results];
        hasMore = currentPage * 10 < response.data.count;
        currentPage++;
      }
      return allPresences;
    } catch (err) {
      console.error("Erreur lors de la récupération des présences", err);
      return [];
    }
  };

  const handleExportPDF = async () => {
    setLoading(true);
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text(`Rapport des présences exceptionnelles - ${currentOrganisation?.nom || ''}`, 14, 20);

      const allPresences = await fetchAllPresences();

      const tableColumn = ["Photo", "Employé", "Activité", "Date", "Heure", "Type"];
      const tableRows = [];

      allPresences.forEach((p) => {
        tableRows.push([
          { content: '', styles: { cellWidth: 20 } },
          `${p.employe_nom} ${p.employe_prenom}`,
          p.activite_nom || '-',
          p.date ? new Date(p.date).toLocaleDateString() : '-',
          p.heure ? p.heure.slice(0, 5) : '-',
          p.type_pointage === 'entree' ? 'Entrée' : 'Sortie'
        ]);
      });

      const totalEntrees = allPresences.filter(p => p.type_pointage === 'entree').length;
      const totalSorties = allPresences.filter(p => p.type_pointage === 'sortie').length;

      doc.setFontSize(12);
      doc.text(`Organisation: ${currentOrganisation?.nom || ''}`, 14, 30);
      doc.text(`Total: ${allPresences.length} | Entrées: ${totalEntrees} | Sorties: ${totalSorties}`, 14, 40);

      autoTable(doc, { 
        head: [tableColumn], 
        body: tableRows, 
        startY: 50, 
        styles: { fontSize: 10 }, 
        headStyles: { fillColor: [27, 1, 155] } 
      });

      const fileName = `presences_exceptionnelles_${currentOrganisation?.nom || ''}_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error("Erreur lors de la génération du PDF", err);
    } finally {
      setLoading(false);
    }
  };

  const renderTypeCell = (type) => {
    return type === 'entree' ? (
      <span className="d-flex align-items-center justify-content-center gap-2">
        <FaSignInAlt style={{ color: '#28a745' }} />
        Entrée
      </span>
    ) : (
      <span className="d-flex align-items-center justify-content-center gap-2">
        <FaSignOutAlt style={{ color: '#dc3545' }} />
        Sortie
      </span>
    );
  };

  return (
    <div style={{...styles.container, marginLeft: sidebarVisible ? 250 : 0}}>
      {/* Modal pour l'image agrandie */}
      {selectedImage && (
        <div style={styles.modalOverlay} onClick={closeImageModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeButton} onClick={closeImageModal}>
              <FaTimes />
            </button>
            <img 
              src={selectedImage.src} 
              alt={selectedImage.name}
              style={styles.modalImage}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/default-avatar.png';
              }}
            />
            <div style={styles.employeeName}>{selectedImage.name}</div>
          </div>
        </div>
      )}

      <div className="text-center mb-3">
        <h3 style={styles.header}>Présences exceptionnelles aux activités</h3>
        <hr style={styles.hr} />
      </div>

      {/* Header avec sélecteur d'organisation réutilisable */}
      <div style={styles.organisationHeader}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />

        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Présences exceptionnelles pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          <div style={styles.filterContainer}>
            <div style={styles.filterGroup}>
              <FaCalendarAlt style={styles.filterIcon} />
              <input
                type="date"
                className="form-control"
                style={styles.searchBar}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>

            <div style={styles.filterGroup}>
              <select
                className="form-control"
                style={styles.select}
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="">Tous les types</option>
                <option value="entree">Entrées</option>
                <option value="sortie">Sorties</option>
              </select>
            </div>

            <div style={{ ...styles.filterGroup, flexGrow: 1 }}>
              <FaSearch style={styles.filterIcon} />
              <input
                type="text"
                className="form-control"
                style={styles.searchBar}
                placeholder="Rechercher un employé..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {(user?.is_super_admin || user?.permissions?.includes("EXPORT_PDF")) && (
            <button
              onClick={handleExportPDF}
              disabled={loading}
              style={styles.exportButton}
            >
              <FaDownload />
              {loading ? 'Génération...' : 'Exporter PDF'}
            </button>
            )}
          </div>

 

          <div className="card shadow-sm mb-4" style={{ borderRadius: '15px', border: 'none' }}>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-center align-middle m-0">
                <thead style={styles.tableHeader}>
                  <tr>
                    <th>Photo</th>
                    <th>Employé</th>
                    <th>Activité</th>
                    <th>Date</th>
                    <th>Heure</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-muted py-4">Chargement en cours...</td>
                    </tr>
                  ) : presences.length > 0 ? (
                    presences.map((p) => (
                      <tr key={p.id}>
                        <td style={styles.photoCell}>
                          {p.photo ? (
                            <img
                              src={p.photo}
                              alt={`${p.employe_prenom} ${p.employe_nom}`}
                              style={styles.photo}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/default-avatar.png';
                              }}
                              onClick={() => openImageModal(p)}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.1)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                              }}
                            />
                          ) : (
                            <div style={styles.defaultPhoto}>
                              {/* Icône par défaut */}
                            </div>
                          )}
                        </td>
                        <td>{p.employe_nom} {p.employe_prenom}</td>
                        <td>{p.activite_nom}</td>
                        <td>{p.date ? new Date(p.date).toLocaleDateString() : '-'}</td>
                        <td>{p.heure ? p.heure.slice(0, 5) : '-'}</td>
                        <td>{renderTypeCell(p.type_pointage)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-muted py-4">Aucune présence exceptionnelle trouvée.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(1)}
                        disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Première
                      </button>
                    </li>
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &laquo;
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            style={page === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &raquo;
                      </button>
                    </li>
                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Dernière
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Sélectionnez une organisation pour commencer</p>
        </div>
      )}
    </div>
  );
};

export default PresencesExceptionnellesPage;